import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchShowAbout } from "../../features/AboutSlice/ShowAboutSlice";
import Loading from "../Loading";
import { useTranslation } from "react-i18next";
import Select from "react-select";
function ShowAboutPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { about, loading, types } = useSelector((state) => state.showAbout);
  const { t, i18n } = useTranslation();
  useEffect(() => {
    dispatch(fetchShowAbout(id));
  }, [id, dispatch, i18n.language]);

  // Animation variants
  const sidebarItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const contentVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };
  const options = [];
  types?.forEach((type) => {
    options.push({ value: type.id, label: t(`${type.value}`) });
  });

    const customStyles = {
    // the pop‑up menu container
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.6)',  // 80% opacity white
      backdropFilter: 'blur(4px)',                  // optional blur
    }),
    // each option in the menu
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'rgba(59, 130, 246, 0.1)'    // 10% blue on hover
        : state.isSelected
          ? 'rgba(59, 130, 246, 0.2)'  // 20% blue when selected
          : 'transparent',
      color: state.isFocused || state.isSelected
        ? '#1E40AF'
        : '#374151',
    }),
    // the closed control (input box)
    control: (provided) => ({
      ...provided,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',  // 90% opacity
    }),
    // ensure the menu renders above other elements
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col lg:flex-row gap-6 w-11/12 md:w-10/12  2xl:w-9/12 xl:w-11/12 lg:w-full px-3 justify-center mx-auto my-10"
    >
      {/* Sidebar Navigation */}
      <div className="w-full lg:w-[25%] hidden lg:block bg-white rounded-lg shadow-lg p-2 h-fit">
        <h1 className="my-2 capitalize ">{t(about?.key)}</h1>
        {types?.length > 0 ? (
          types.map((type, index) => (
            <motion.div
              key={type.id}
              variants={sidebarItemVariants}
              initial="hidden"
              animate="visible"
              transition={{
                delay: index * 0.1,
                duration: 0.3,
              }}
              onClick={() => navigate(`/about/${type.id}`)}
              className={`p-3 mb-2 rounded-md transition-colors cursor-pointer hover:bg-blue-50 ${about?.id === type.id
                  ? "border-l-4 border-[#07A024] bg-[#07A024]/20 font-semibold duration-700"
                  : "hover:border-l-4 hover:border-[#07A024] hover:bg-[#07A024]/20 duration-700"
                }`}
            >
              <h2 className="text-gray-700 text-sm md:text-base">
                {t(`${type.value}`)}
              </h2>
            </motion.div>
          ))
        ) : (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                className="h-10 bg-gray-100 rounded-md animate-pulse"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="block lg:hidden w-full mx-auto">
        <Select
          options={options}
          isClearable={false}
          isSearchable={false}
          value={options.find((option) => option.value === about?.id)}
          onChange={(selectedOption) => navigate(`/about/${selectedOption.value}`)}
          className="w-full"
          styles={customStyles}
        />

      </div>

      {/* Content Area */}
      <motion.div
        className="w-full lg:w-[75%] bg-white rounded-lg shadow-lg p-6 min-h-[400px]"
        variants={contentVariants}
        initial="hidden"
        animate="visible"
        transition={{ delay: 0.2 }}
      >
        {loading ? (
          <Loading />
        ) : (
          <motion.article
            className="prose max-w-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              dangerouslySetInnerHTML={{ __html: about?.description }}
              className="text-gray-600 leading-relaxed"
            />
          </motion.article>
        )}
      </motion.div>
    </motion.div>
  );
}

export default ShowAboutPage;
